/**
 * @lines98/client/screens/settings-screen — страница настроек игры.
 *
 * «Веточка из дерева» (CLAUDE.md «ствол → ветки», brief-39 §C): экран — ТОНКАЯ
 * ветка, которая монтирует ОБЩИЕ модули и задаёт только внешний вид. Логику строки
 * настройки несёт `components/setting-row`, контрол — `components/toggle-switch`,
 * заголовок — `lib/title-fit` (`.screen-title`, единый размер), описание-копия —
 * `components/info-popover` (через setting-row). Никакой дублирующей UI-логики тут нет.
 *
 * РАСШИРЯЕМЫЙ список (owner «задел под будущее»): настройки описаны декларативно в
 * ОБЩЕМ `lib/settings-list` (тот же источник, что у settings-drawer §5.5 — не
 * дублируем) — добавить пункт (дальтоники / темы / звук — БУДУЩЕЕ, НЕ сейчас) =
 * добавить ещё один `createSettingRow(...)` ТАМ, БЕЗ переделки раскладки. Сейчас —
 * только тумблер live-анимации (brief-39 §B).
 *
 * ПРИМЕЧАНИЕ (T-39R-B3): первичная точка входа в настройки теперь — выезжающий
 * settings-drawer (`components/settings-drawer`), открываемый из nav-rail «Настройки»
 * (работает и во время партии, не меняя фазу). Этот экран остаётся для viewport-
 * baseline `?demo=settings`; контент строит тот же `buildSettingRows()`.
 *
 * Источник правды настройки — `lib/client-prefs` (единый версионированный модуль,
 * персист между сессиями). Тумблер читает текущее значение на mount и пишет через
 * `setPref` на каждое переключение.
 *
 * Навигация: вход — через глобальный nav-rail (пункт «Настройки»); выход — назад
 * на предыдущий экран (кнопка «Назад» + nav-rail). Layout/visual — styles/settings.css.
 *
 * Spec/skill: responsive-ui-design SKILL §0 (priority hierarchy) + §5.0 layout-plan
 * (в шапке settings.css) + §0.B/§0.E (labels must-visible / descriptions за «?»).
 */

import '../styles/settings.css';

import { type SettingRowHandle } from '../components/setting-row.js';
import { type AutofitHandle } from '../lib/autofit-text.js';
import { buildSettingRows } from '../lib/settings-list.js';
import { attachTitleFit } from '../lib/title-fit.js';
import { t } from '../lib/i18n/index.js';
import { useGameStore } from '../store/game-store.js';
import type { ScreenHandle } from './board-screen.js';

/**
 * Смонтировать экран настроек.
 */
export function mountSettingsScreen(host: HTMLElement): ScreenHandle {
  host.innerHTML = '';
  const root = document.createElement('div');
  // .screen-root — ствол-класс edge-clearance (safe-area-aware, global.css).
  root.className = 'settings-screen screen-root';
  root.innerHTML = `
    <header class="settings-header">
      <h2 class="settings-title screen-title"></h2>
    </header>
    <div class="settings-list" role="list"></div>
    <div class="settings-foot">
      <button class="btn settings-back" type="button"></button>
    </div>
  `;
  host.appendChild(root);

  // Подписи через `t()` ПРИ mount (textContent/aria — безопасно от спецсимволов в
  // переводе) — фаза «settings» ре-монтируется на смену locale (app.ts
  // gamePhaseLangChanged), поэтому строки следуют активному языку.
  root.querySelector<HTMLElement>('.settings-title')!.textContent = t('game.settingsScreen.title');
  const listEl = root.querySelector<HTMLElement>('.settings-list')!;
  listEl.setAttribute('aria-label', t('game.settingsScreen.listAria'));
  root.querySelector<HTMLButtonElement>('.settings-back')!.textContent = t('game.settingsScreen.back');
  const rows: SettingRowHandle[] = buildSettingRows();
  for (const r of rows) {
    r.element.setAttribute('role', 'listitem');
    listEl.appendChild(r.element);
  }

  // «Назад» → предыдущий экран. Минимальный консистентный выход: возврат на главную
  // (доска дня) store-действием (in-shell, без перезагрузки). nav-rail остаётся.
  const backBtn = root.querySelector<HTMLButtonElement>('.settings-back')!;
  backBtn.addEventListener('click', () => {
    useGameStore.getState().showDaily();
  });

  const titleFit: AutofitHandle = attachTitleFit(root);

  return {
    unmount(): void {
      titleFit.detach();
      for (const r of rows) r.destroy();
      host.innerHTML = '';
    },
  };
}
